import React, {
  useRef,
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import api from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import { FilterContext } from '../contexts/FilterContext';
import { DialogPlanejamento } from './DialogPlanejamento';
import { DialogDescricaoPlanejamento } from './DialogDescricaoPlanejamento';
import { PermissaoLoader } from './PermissaoLoader';
import { permissions_check } from '../utils/rotas_permissao';
import '../layout/sass/_pickListSugestoes.scss';
import '../layout/sass/_responsiveTable.scss';

export const TabelaPlanejamento = () => {
  const toast = useRef(null);
  const [planejamentosTotal, setPlanejamentosTotal] = useState([]);
  const [planejamentos, setPlanejamentos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dt, setDt] = useState(null);
  const history = useHistory();
  const [showListaPlanejamentos, setShowListaPlanejamentos] = useState(false);
  const { user, permissoes } = useContext(AuthContext);
  const { filter, setFilter } = useContext(FilterContext);
  const [sortable, setSortable] = useState('ASC');
  const [sortableCampo, setSortableCampo] = useState('id');
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [descricaoVisible, setDescricaoVisible] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [showActionsColumn, setShowActionsColumn] = useState(true);
  const isInstrutor = user.perfilUser.includes('user-instrutor');

  useEffect(() => {
    if (user.perfilUser.includes('user-instrutor')) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        filters: {
          ...prevFilter.filters,
          instrutorResponsavel: {
            value: user.nome,
            matchMode: 'contains',
          },
        },
      }));
    }
  }, []);

  const header = (
    <div className="p-grid p-col-12 p-m-0 p-p-0">
      <div>
        <h2 className="p-my-0">Planejamentos</h2>
      </div>
      <div className="p-my-0 p-grid p-dir-rev p-col">
        <div className="p-m-2">
          <PermissaoLoader
            rota="planejamentos"
            acao="criar"
            onClick={() => history.push('/planejamentos/create')}
          />
        </div>
        <div className="p-m-2">
          <Button
            label="Limpar Filtros"
            className="p-mb-2 p-button-primary"
            iconPos="left"
            type="button"
            onClick={() => handleLimparClick()}
          />
        </div>
      </div>
    </div>
  );

  const handleLimparClick = () => {
    setFilter({
      first: 0,
      rows: 20,
      sortField: null,
      sortOrder: null,
      filters: isInstrutor
        ? {
          instrutorResponsavel: {
            value: user.nome,
            matchMode: 'contains',
          },
        }
        : {},
    });
  };

  const getPlanejamentos = useCallback(
    async (event) => {
      setIsLoading(true);
      setPlanejamentos([]);
      if (event !== undefined) {
        setTimeout(() => {
          const startIndex = event.first;
          setFirst(startIndex);
        }, 250);
      }

      try {
        let response;
        const page = event ? event.page : 0;

        // Check if we have filters OR if user is instructor
        if (
          (filter.filters && Object.keys(filter.filters).length > 0) ||
          isInstrutor
        ) {
          const pesquisa = {
            id: filter.filters?.['id']?.value || null,
            descricao: filter.filters?.['descricao']?.value || '',
            instrutorResponsavelNome: isInstrutor
              ? user.nome
              : filter.filters?.['instrutorResponsavel']?.value || '',
          };

          response = await api.post(
            `/planejamento/searchPlanejamento/${sortable}/${sortableCampo}?page=${page}`,
            pesquisa
          );
        } else {
          response = await api.get(
            `/planejamento?page=${page}&sort=${sortableCampo},${sortable}`
          );
        }

        setPlanejamentos(response.data.content);
        setTotalRecords(response.data.totalElements);
      } catch (error) {
        console.error('Erro ao obter planejamentos:', error.response || error);
        toast.current.show({
          severity: 'error',
          summary: 'Erro ao obter planejamentos',
          detail: error.response?.data?.message || error.message,
          life: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [filter, sortable, sortableCampo, isInstrutor, user.nome]
  );

  const getPlanejamentosAll = useCallback(async () => {
    try {
      const response = await api.get(
        isInstrutor
          ? `/planejamento/planejamentoAllInstrutor/${user.email}`
          : '/planejamento/planejamentoAll'
      );
      setPlanejamentosTotal(response.data);
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Erro ao obter planejamentos',
        detail: error.response?.data?.message || error.message,
        life: 3000,
      });
    }
  }, [isInstrutor, user.email]);

  // Second useEffect: Fetch data only after filter is initialized
  useEffect(() => {
    getPlanejamentos();
    getPlanejamentosAll();
  }, [getPlanejamentos, getPlanejamentosAll]);

  const actionBodyTemplate = (rowData) => {
    const hasEditPermission = permissions_check(
      'planejamentos',
      'editar',
      permissoes
    );

    if (!hasEditPermission) {
      setShowActionsColumn(false);
    } else {
      return (
        <div className="actions">
          {hasEditPermission && (
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-mr-2 edit-btn"
              onClick={() => handleEditClick(rowData)}
            />
          )}
        </div>
      );
    }
  };

  const idBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <p style={{ textAlign: 'left' }}>
          <span className="p-column-title">Código</span>
          <span className="table-data">{rowData.id}</span>
        </p>
      </React.Fragment>
    );
  };

  const descricaoBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <p style={{ textAlign: 'left' }}>
          <span className="p-column-title">Descrição</span>
          <span
            className="table-data"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setDescricao(rowData.descricao);
              setDescricaoVisible(true);
            }}
          >
            {rowData.descricao?.substring(0, 30) + '...'}
          </span>
        </p>
      </React.Fragment>
    );
  };

  const turmasBodyTemplate = (rowData) => {
    const turmas = rowData?.turmasAnoAtual ? rowData.turmasAnoAtual.split(',') : ['—'];
    return (
      <React.Fragment>
        <p style={{ textAlign: 'left' }}>
          <span className="p-column-title">Turmas</span>
          <span className="table-data">
            {turmas.map((turma, index) => (
              <div key={index} style={{ marginBottom: '2rem' }}>
                {turma}
              </div>
            ))}
          </span>
        </p>
      </React.Fragment>
    );
  };

  const vigenciaBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <p style={{ textAlign: 'left' }}>
          <span className="p-column-title">Vigência</span>
          <span className="table-data">
            {rowData.vigencia
              ? moment(rowData.vigencia).format('DD/MM/YYYY')
              : ''}
          </span>
        </p>
      </React.Fragment>
    );
  };

  const instrutorBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <p style={{ textAlign: 'left' }}>
          <span className="p-column-title">Instrutor</span>
          <span className="table-data">
            {rowData.instrutorResponsavel?.nome ||
              rowData.instrutorResponsavel?.email ||
              'Sem Nome'}
          </span>
        </p>
      </React.Fragment>
    );
  };

  const updatedAtBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <p style={{ textAlign: 'left' }}>
          <span className="p-column-title">Atualizado Em</span>
          <span className="table-data">
            {rowData.updatedAt
              ? new Date(rowData.updatedAt).toLocaleDateString('pt-BR')
              : ''}
          </span>
        </p>
      </React.Fragment>
    );
  };

  const onSort = (event) => {
    setSortableCampo(event.sortField);
    setSortable(event.sortOrder === 1 ? 'ASC' : 'DESC');
  };

  const onFilter = (event) => {
    event['first'] = 0;
    setFilter(event);
  };

  const handleEditClick = (data) => {
    history.push(`/planejamentos/edit/${data.id}`);
  };

  const handlePrintClick = () => {
    setShowListaPlanejamentos(true);
  };

  return (
    <div className="card p-grid p-col-12 p-mx-0 p-p-0 p-mt-0">
      <DialogPlanejamento
        visible={showListaPlanejamentos}
        setVisible={setShowListaPlanejamentos}
        planejamento={planejamentosTotal}
      />
      <div className="p-my-0 p-dir-rev p-col">
        <div className="p-my-0">
          <Button
            icon="pi pi-print"
            className="p-button-secondary"
            style={{ width: '35px' }}
            onClick={handlePrintClick}
            tooltip="Gerar relatório"
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      </div>
      <Toast ref={toast} position="bottom-right" />
      <DialogDescricaoPlanejamento
        visible={descricaoVisible}
        setVisible={setDescricaoVisible}
        descricao={descricao}
      />
      <div className="card p-col-12 datatable-responsive-demo">
        <div className="p-fluid">
          <DataTable
            totalRecords={totalRecords}
            lazy={true}
            first={first}
            onPage={getPlanejamentos}
            ref={(element) => setDt(element)}
            header={header}
            value={planejamentos}
            filterDisplay="row"
            className="p-datatable-responsive-demo"
            emptyMessage="Nenhum planejamento encontrado"
            loading={isLoading}
            dataKey="id"
            paginator
            rows={20}
            currentPageReportTemplate={`Mostrando de {first} a {last} de ${totalRecords} planejamentos`}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageReport"
            onSort={onSort}
            onFilter={onFilter}
            filters={filter.filters}
          >
            <Column
              field="id"
              header="Código"
              sortable={true}
              body={idBodyTemplate}
              filter
              filterMatchMode="contains"
              filterPlaceholder={
                filter.filters?.id?.value || 'Pesquisar pelo Código'
              }
            />
            <Column
              field="vigencia"
              header="Vigência"
              sortable={true}
              body={vigenciaBodyTemplate}
              filter
              filterMatchMode="contains"
              filterPlaceholder={
                filter.filters?.vigencia?.value || 'Pesquisar pela vigência'
              }
            />
            <Column
              field="descricao"
              header="Descrição"
              sortable={true}
              body={descricaoBodyTemplate}
              filter
              filterMatchMode="contains"
              filterPlaceholder={
                filter.filters?.descricao?.value || 'Pesquisar pela descrição'
              }
            />
            <Column
              field="turmasAnoAtual"
              header="Turmas"
              //sortable={true}
              body={turmasBodyTemplate}
            //filter
            //filterMatchMode="contains"
            //filterPlaceholder={
            //  filter.filters?.turmasAnoAtual?.value || 'Pesquisar pela turma'
            //}
            />
            <Column
              field="instrutorResponsavel"
              header="Instrutor"
              sortable={true}
              body={instrutorBodyTemplate}
              filter={!isInstrutor}
              filterMatchMode="contains"
              filterPlaceholder={
                filter.filters?.instrutorResponsavel?.value ||
                'Pesquisar pelo instrutor'
              }
            />
            <Column
              field="updatedAt"
              header="Atualizado Em"
              sortable={true}
              body={updatedAtBodyTemplate}
            />
            {showActionsColumn && (
              <Column header="Ações" body={actionBodyTemplate} />
            )}
          </DataTable>
        </div>
      </div>
    </div>
  );
};
